import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Form } from '@components/atoms/Form';
import { Card } from '@components/atoms/Card';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography } from '@components/atoms/Typography';
import { PopOver } from '@components/atoms/PopOver';
import { Modules, useModule } from '@hooks/useModule';
import { useApplyPromoCode } from '@services/useApplyPromoCode';
import { useRemovePromoCode } from '@services/useRemovePromoCode';
import styles from './DiscountCodeForm.module.css';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { useData } from '@hooks/useData';

interface DiscountCodeFormErrors {
    promoCode: string[];
}

export const DiscountCodeForm: FC = () => {
    const { request: requestApplyPromoCode, error: errorApply, lastResponseTime: lastApply } = useApplyPromoCode();
    const { request: requestRemovePromoCode, lastResponseTime: lastRemove } = useRemovePromoCode();
    const { cart } = useData();
    const { t } = useTranslation();

    const translations = {
        title: t('discount-code-form-component.title'),
        discountCodePlaceholder: t('discount-code-form-component.discount-code.placeholder'),
        discountCodeRequiredError: t('discount-code-form-component.discount-code.required-error'),
        discountCodeInvalidError: t('discount-code-form-component.discount-code.invalid-error'),
        submitButton: t('discount-code-form-component.submit-button'),
        cancelButton: t('discount-code-form-component.cancel-button'),
    };

    const formRef = useRef<HTMLFormElement>(null);
    const [promoCode, setPromoCode] = useState<string>('');

    // errors to be displayed in the form
    const [errors, setErrors] = useState<DiscountCodeFormErrors>({
        promoCode: [],
    });

    const handleOnClickApplyCoupon = () => {
        const errors = validateForm();

        if (errors.promoCode.length === 0) {
            formRef?.current?.requestSubmit();
        }
    };

    const handleOnClickRemoveCoupon = () => {
        requestRemovePromoCode();
    };

    // clear error after typing again
    useEffect(() => {
        if (promoCode.length > 0) {
            clearErrors();
        }
    }, [promoCode]);

    const clearErrors = () => {
        setErrors({ promoCode: [] });
    };

    const validateForm: () => DiscountCodeFormErrors = () => {
        const newErrors: DiscountCodeFormErrors = {
            promoCode: [],
        };

        !promoCode.trim() && newErrors.promoCode.push(translations.discountCodeRequiredError);

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = () => {
        requestApplyPromoCode({ promo_code: promoCode });
    };

    const { isModuleActive } = useModule();

    useEffect(() => {
        if (lastApply && errorApply) {
            setErrors({ promoCode: errorApply ? [errorApply] : [] });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastApply]);

    // clear input
    useEffect(() => {
        if (lastRemove) {
            setPromoCode('');
            clearErrors();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastRemove]);

    return (
        <>
            {isModuleActive(Modules.coupon) && (
                <Card classes={styles['discount-code-card']}>
                    <Typography classes="mb-2" fontSize="16" fontWeight="800">
                        {translations.title}
                    </Typography>
                    <Form formRef={formRef} onSubmit={submitForm}>
                        <div className="d-flex gap-3 pt-1">
                            <div className={styles['input-container']}>
                                {cart?.data?.promotion?.code ? (
                                    <Input
                                        type={InputTypes.text}
                                        variant={InputVariants.regular}
                                        value={cart?.data?.promotion?.code}
                                        onChange={() => {}}
                                        state={cart?.data?.promotion?.isValidCode ? InputStates.success : InputStates.error}
                                    />
                                ) : (
                                    <>
                                        <Input
                                            type={InputTypes.text}
                                            placeholder={translations.discountCodePlaceholder}
                                            variant={InputVariants.regular}
                                            value={promoCode}
                                            onChange={(value) => setPromoCode(value)}
                                        />

                                        {errors.promoCode.length > 0 && promoCode.length === 0 && (
                                            <PopOver
                                                title={translations.discountCodeRequiredError}
                                                trigger="click"
                                                placement="bottom"
                                                initialShow={true}
                                                closeButton={true}
                                                onClickCloseButton={() => setErrors({ promoCode: [] })}>
                                                <div className="w-100" style={{ height: '1px' }}></div>
                                            </PopOver>
                                        )}
                                    </>
                                )}
                            </div>

                            {cart?.data?.promotion?.code ? (
                                <Button
                                    mode={ButtonMode.danger}
                                    label={translations.cancelButton}
                                    size="sm"
                                    onClick={() => handleOnClickRemoveCoupon()}
                                />
                            ) : (
                                <Button
                                    mode={ButtonMode.primary}
                                    label={translations.submitButton}
                                    size="sm"
                                    onClick={() => handleOnClickApplyCoupon()}
                                />
                            )}
                        </div>
                        <>
                            {cart?.data?.promotion?.isValidCode === false ? (
                                <ErrorMessages messages={[translations.discountCodeInvalidError]} />
                            ) : (
                                <>
                                    {!cart?.data?.promotion?.code && errors.promoCode.length > 0 && promoCode.length > 0 && (
                                        <ErrorMessages messages={errors.promoCode} />
                                    )}
                                </>
                            )}
                        </>
                    </Form>
                </Card>
            )}
        </>
    );
};
